import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { AlertTriangle, LogOut, RefreshCw } from 'lucide-react'
import { useAuth } from './AuthContext'

interface Props {
  errorMessage: string
  onRetry: () => Promise<void> | void
}

export default function InviteCheckErrorScreen({ errorMessage, onRetry }: Props) {
  const { t } = useTranslation('auth')
  const { signOut, user } = useAuth()
  const [retrying, setRetrying] = useState(false)

  const handleRetry = async () => {
    setRetrying(true)
    try {
      await onRetry()
    } finally {
      setRetrying(false)
    }
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-full px-8 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-10">
          <img src="/tendbot.png" alt="TendBot" className="h-20 w-auto mx-auto mb-3" />
          <h1 className="font-display text-4xl text-gold font-semibold mb-2">TendBot</h1>
        </div>

        <div className="glass-card rounded-2xl p-6 glow-gold text-center">
          <div className="w-14 h-14 rounded-full bg-rose/10 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={24} className="text-rose" />
          </div>

          <h2 className="font-display text-xl text-ivory font-semibold mb-3">
            {t('inviteCheckFailed')}
          </h2>
          <p className="text-pearl/80 text-sm leading-relaxed mb-2">
            {t('inviteCheckFailedDescription')}
          </p>
          {user?.email && (
            <p className="text-pearl/60 text-xs mb-4">
              {user.email}
            </p>
          )}

          <div className="flex items-start gap-2 text-left text-xs text-rose/90 bg-rose/10 border border-rose/20 rounded-lg p-3 mb-4">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span className="break-words">{errorMessage}</span>
          </div>

          <button
            onClick={handleRetry}
            disabled={retrying}
            className="w-full py-3 rounded-xl bg-gold text-midnight font-medium text-sm hover:bg-gold/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-60 mb-3"
          >
            <RefreshCw size={14} className={retrying ? 'animate-spin' : ''} />
            {t('tryAgain')}
          </button>

          <button
            onClick={() => signOut()}
            className="w-full py-3 rounded-xl bg-slate-mid/30 border border-gold/10 text-pearl/80 font-medium text-sm hover:bg-slate-mid/50 transition-colors flex items-center justify-center gap-2"
          >
            <LogOut size={14} />
            {t('signOutAndTryAnother')}
          </button>
        </div>
      </motion.div>
    </div>
  )
}
